var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _FsSsnField_instances, _FsSsnField_onKeydown, _FsSsnField_onInvalid, _FsSsnField_setInstanceAndFormValue;
import 'element-internals-polyfill';
import labelStyles from '@cof/fs-field-label-core/lib/field-label.lit.min.js';
import formFieldStyles from '@cof/fs-form-field-core/lib/form-field.lit.min.js';
import helperTextStyles from '@cof/fs-helper-text-core/lib/helper-text.lit.min.js';
import { ssnFieldEventType, SsnFieldManager, ssnFieldManagerConfigDefaults } from '@cof/fs-ssn-field-core-beta';
import styles from '@cof/fs-text-field-core/lib/text-field.lit.min.js';
import { FormControlMixin, requiredValidator } from '@open-wc/form-control';
import { submit } from '@open-wc/form-helpers';
import { css, html, LitElement, nothing } from 'lit';
import { property, query } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { patternValidatorSsn, tooLongValidatorSsn, tooShortValidatorSsn } from './validators.js';
/**
 *
 * @element fs-ssn-field
 */
export class FsSsnField extends FormControlMixin(LitElement) {
    constructor() {
        super(...arguments);
        _FsSsnField_instances.add(this);
        /** The autocomplete settings for the input */
        this.autocomplete = 'off';
        /** If true, the browser should automatically focus on the input */
        this.autofocus = false;
        /** If true, the input will use compact styles */
        this.compact = false;
        /** When set to true the element will not respect pattern validations */
        this.dangerouslyDisablePatternValidation = ssnFieldManagerConfigDefaults.dangerouslyDisablePatternValidation;
        /** If true, the input will be disabled */
        this.disabled = false;
        /**
         * If set, helper text will be displayed below the input
         * @attribute helper-text
         */
        this.helperText = '';
        /**
         * The message to use for the button name and tooltip when prompting
         * the user to hide the social security number
         */
        this.hideMessage = ssnFieldManagerConfigDefaults.hideMessage;
        /**
         * If true, the label will be visually hidden but remain for a11y purposes
         * @attribute hide-label
         */
        this.hideLabel = false;
        /**
         * The character that will be used to data mask the first five characters
         * of the social security number
         */
        this.maskedCharacter = ssnFieldManagerConfigDefaults.maskedCharacter;
        this.maxLength = undefined;
        /**
         * If true, the input will not use input validators
         * @attribute no-validate
         */
        this.noValidate = false;
        /** The placeholder string on the input element. Should not be relied upon as a label */
        this.placeholder = '';
        /**
         * Allows the user to override the default pattern mismatch error message
         */
        this.patternMismatchMessage = 'This does not match the approved pattern for a social security number.';
        /**
         * A proxy for the input's readonly attribute
         * @attribute readonly
         */
        this.readOnly = false;
        /** If true, the input is required if used in the context of a form */
        this.required = false;
        /** The character used to separate common groups in the SSN field */
        this.separator = ssnFieldManagerConfigDefaults.separator;
        /** Should the error be shown */
        this.showError = false;
        /**
         * The message to use for the button name and tooltip when prompting
         * the user to show the social security number
         */
        this.showMessage = ssnFieldManagerConfigDefaults.showMessage;
        /**
         * When set to true the social security number field will use a formatted
         * value when participating in form submission
         */
        this.useMaskedValue = ssnFieldManagerConfigDefaults.useMaskedValue;
        /** Allows the developer to override the default value missing error message */
        this.valueMissingMessage = 'A social security number is required';
        /**
         * Whenever the control identifies as invalid, we need to cancel
         * the default behavior of popping up the native validation display
         * and focus the element.
         */
        _FsSsnField_onInvalid.set(this, (event) => {
            event.preventDefault();
            this.focus();
        });
        /**
         * Set the instance's value property and use the FormControlMixin's
         * setValue method to set the native form value
         */
        _FsSsnField_setInstanceAndFormValue.set(this, (event) => {
            this.value = this.unmaskedValue;
            this.setValue(this.manager.preferredValue);
            if (event.type === 'change') {
                this.dispatchEvent(new Event(event.type, event));
            }
        });
    }
    /** Styles for the text field */
    static get styles() {
        return [
            styles,
            labelStyles,
            formFieldStyles,
            helperTextStyles,
            css `
      :host { display: block; }
    `
        ];
    }
    get maskedValue() {
        return this.manager.formattedValue;
    }
    get unmaskedValue() {
        return this.manager.unFormattedValue;
    }
    get managerConfig() {
        return {
            dangerouslyDisablePatternValidation: this.dangerouslyDisablePatternValidation,
            hideMessage: this.hideMessage,
            maskedCharacter: this.maskedCharacter,
            separator: this.separator,
            showMessage: this.showMessage,
            useMaskedValue: this.useMaskedValue
        };
    }
    /**
     * Invoked when the component is added to the document's DOM.
     * This connects (or reconnects) the SsnrFieldManager to the input and adds needed event listeners.
     */
    connectedCallback() {
        super.connectedCallback();
        if (this.manager != null && !this.manager.isConnected) {
            this.manager.connect();
            this.manager.addEventListener(ssnFieldEventType['ssn-field-change'], __classPrivateFieldGet(this, _FsSsnField_setInstanceAndFormValue, "f"));
        }
        this.addEventListener('invalid', __classPrivateFieldGet(this, _FsSsnField_onInvalid, "f"));
    }
    /**
     * Invoked when the component is removed from the document's DOM.
     * This disconnects the SsnFieldManager from the input and removes event listeners.
     */
    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.manager?.isConnected) {
            this.manager.disconnect();
        }
        this.manager.removeEventListener(ssnFieldEventType['ssn-field-change'], __classPrivateFieldGet(this, _FsSsnField_setInstanceAndFormValue, "f"));
        this.removeEventListener('invalid', __classPrivateFieldGet(this, _FsSsnField_onInvalid, "f"));
    }
    /**
     * When the field is first updated, create a new instances of the manager
     * and connect it to the DOM. We also want to check for any initial value and
     * process that using the manager and set the form value as the provided value.
     */
    firstUpdated() {
        this.manager = new SsnFieldManager(this.renderRoot.querySelector('.fs-form-field'));
        this.manager.connect();
        if (this.value) {
            this.manager.setValue(this.value);
            this.setValue(this.value);
        }
        else {
            this.setValue('');
        }
        this.tabIndex = 0;
        this.manager.addEventListener(ssnFieldEventType['ssn-field-change'], __classPrivateFieldGet(this, _FsSsnField_setInstanceAndFormValue, "f"));
        setTimeout(() => {
            // TODO: workaround to set aria-toggle to false on initial component load
            // we should be able to remove the internal button and set the aria toggle attribute on the host element instead
            this.manager.toggleMaskBtn.shadowRoot.querySelector('button').setAttribute('aria-pressed', 'false');
        }, 0);
    }
    render() {
        return html `
        <div class="fs-form-field ${this.compact ? 'fs-form-field--compact' : ''}">
          <label
            class="
              fs-field-label
              ${this.compact ? 'fs-field-label--compact' : ''}
              ${this.disabled ? 'fs-field-label--disabled' : ''}
              ${this.hideLabel ? 'fs-field-label--hidden' : ''}
            "
            for="input"
          >
            <slot></slot> ${this.required ? '' : html `<span class="fs-field-label__optional">(optional)</span>`}
          </label>
        <div
          class="
            fs-text-field
            ${this.compact ? 'fs-text-field--compact' : ''}
            ${this.disabled ? 'fs-text-field--disabled' : ''}
            ${this.showError ? 'fs-text-field--error' : ''}
            ${this.readOnly ? 'fs-text-field--readonly' : ''}
          "
        >
          <slot name="leading-icon"></slot>
          <input
            id="input"
            autocomplete="${this.autocomplete}"
            aria-describedby="helper-text"
            ?autofocus="${this.autofocus}"
            ?disabled="${this.disabled}"
            maxlength="${ifDefined(this.maxLength)}"
            name="${this.name}"
            placeholder="${this.placeholder}"
            ?readonly="${this.readOnly}"
            ?required="${this.required}"
            @change="${__classPrivateFieldGet(this, _FsSsnField_setInstanceAndFormValue, "f")}"
            @input="${__classPrivateFieldGet(this, _FsSsnField_setInstanceAndFormValue, "f")}"
            @keydown="${__classPrivateFieldGet(this, _FsSsnField_instances, "m", _FsSsnField_onKeydown)}"
            @paste="${__classPrivateFieldGet(this, _FsSsnField_setInstanceAndFormValue, "f")}"
            part="fs-input__input"
          >
        </div>
        ${this.noValidate
            ? nothing
            : html `
                <span
                  class="fs-helper-text ${this.showError && !this.disabled ? 'fs-helper-text--error' : ''} ${this.disabled ? 'fs-helper-text--disabled' : ''}"
                  id="helper-text"
                  aria-atomic="true"
                >
                  ${!this.showError || this.disabled ? this.helperText : ''}
                  <span aria-atomic="true" role="alert">${this.showError && !this.disabled ? this.validationMessage : ''}</span>
                </span>`}
      </div>`;
    }
    /** Lit lifecycle Respond to changes */
    updated(changed) {
        /**
         * When the value property or attribute is set not in alignment with
         * the firstUpdated lifecycle method, respond by setting the manager's value
         * alongside the FormControl's value to trigger validations.
         */
        if (changed.has('value') && this.value !== this.unmaskedValue) {
            this.manager.setValue(this.value);
            this.setValue(this.unmaskedValue);
        }
        /** Update the manager configuration if any config key changes */
        if (changed.has('dangerouslyDisablePatternValidation') ||
            changed.has('hideMessage') ||
            changed.has('maskedCharacter') ||
            changed.has('separator') ||
            changed.has('showMessage') ||
            changed.has('useMaskedValue')) {
            this.manager.updateConfig(this.managerConfig);
        }
        if (changed.has('useMaskedValue')) {
            this.setValue(this.manager.preferredValue);
        }
    }
    /**
     * Override the default validator messages by keying off the provided
     * validationKey argument
     */
    validityCallback(validationKey) {
        switch (validationKey) {
            case 'valueMissing':
                return this.valueMissingMessage;
            case 'patternMismatch':
                return this.patternMismatchMessage;
            default:
                return;
        }
    }
    /**
     * Lifecycle method from the FormControlMixin
     *
     * Here we capture any updates to the validation message coming from
     * the validators and set the error and override the helper text
     */
    validationMessageCallback(message) {
        this.validationMessage = message;
        this.showError = !!message;
    }
}
_FsSsnField_onInvalid = new WeakMap(), _FsSsnField_setInstanceAndFormValue = new WeakMap(), _FsSsnField_instances = new WeakSet(), _FsSsnField_onKeydown = function _FsSsnField_onKeydown(event) {
    if (event.code === 'Enter' && this.form) {
        submit(this.form);
    }
};
/** SSN field validators */
FsSsnField.formControlValidators = [
    requiredValidator,
    tooShortValidatorSsn,
    tooLongValidatorSsn,
    patternValidatorSsn
];
/** Set the canonical name for use with the safe define utility */
FsSsnField.canonicalName = 'ssn-field';
/** Delegate the focus to the internal input (this.validationTarget) */
FsSsnField.shadowRootOptions = {
    delegatesFocus: true,
    mode: 'open'
};
__decorate([
    property()
], FsSsnField.prototype, "autocomplete", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsSsnField.prototype, "autofocus", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsSsnField.prototype, "compact", void 0);
__decorate([
    property({ type: Boolean, attribute: 'dangerously-disable-pattern-validation' })
], FsSsnField.prototype, "dangerouslyDisablePatternValidation", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsSsnField.prototype, "disabled", void 0);
__decorate([
    property({ type: String, attribute: 'helper-text' })
], FsSsnField.prototype, "helperText", void 0);
__decorate([
    property({ type: String, attribute: 'hide-message', reflect: false })
], FsSsnField.prototype, "hideMessage", void 0);
__decorate([
    property({ type: Boolean, attribute: 'hide-label' })
], FsSsnField.prototype, "hideLabel", void 0);
__decorate([
    property({ type: String, attribute: 'masked-character', reflect: false })
], FsSsnField.prototype, "maskedCharacter", void 0);
__decorate([
    property({ type: Number, attribute: 'maxlength' })
], FsSsnField.prototype, "maxLength", void 0);
__decorate([
    property({ type: String, reflect: true })
], FsSsnField.prototype, "name", void 0);
__decorate([
    property({ type: Boolean, attribute: 'no-validate' })
], FsSsnField.prototype, "noValidate", void 0);
__decorate([
    property()
], FsSsnField.prototype, "placeholder", void 0);
__decorate([
    property({ type: String, attribute: 'pattern-mismatch-message', reflect: false })
], FsSsnField.prototype, "patternMismatchMessage", void 0);
__decorate([
    property({ type: Boolean, attribute: 'readonly' })
], FsSsnField.prototype, "readOnly", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsSsnField.prototype, "required", void 0);
__decorate([
    property({ type: String, reflect: false })
], FsSsnField.prototype, "separator", void 0);
__decorate([
    property({ type: Boolean, reflect: false })
], FsSsnField.prototype, "showError", void 0);
__decorate([
    property({ type: String, attribute: 'show-message', reflect: false })
], FsSsnField.prototype, "showMessage", void 0);
__decorate([
    property({ type: Boolean, attribute: 'use-masked-value' })
], FsSsnField.prototype, "useMaskedValue", void 0);
__decorate([
    property()
], FsSsnField.prototype, "value", void 0);
__decorate([
    property({ type: String, reflect: false })
], FsSsnField.prototype, "validationMessage", void 0);
__decorate([
    property({ type: String, attribute: 'value-missing-message', reflect: false })
], FsSsnField.prototype, "valueMissingMessage", void 0);
__decorate([
    query('input')
], FsSsnField.prototype, "validationTarget", void 0);
//# sourceMappingURL=SsnField.js.map